import dagre from '@dagrejs/dagre'
import type { Edge } from '@xyflow/react'
import type { LayoutBox } from 'app/types/workflow-layout'
import { FIXED_REPLY_EXITS } from './replyKeywords'
import type { WorkflowNode } from 'app/types/workflows'

const GRID = 20
const FALLBACK_WIDTH = 240
const FALLBACK_HEIGHT = 100
const NODESEP = 60

const snap = (value: number) => Math.round(value / GRID) * GRID

/** All nodes reachable downstream of (and including) startId, following outgoing edges. */
export function collectDownstream(startId: string, edges: readonly Edge[]): Set<string> {
  const visited = new Set<string>()
  const stack = [startId]
  while (stack.length > 0) {
    const id = stack.pop()
    if (id === undefined || visited.has(id)) continue
    visited.add(id)
    for (const edge of edges) if (edge.source === id) stack.push(edge.target)
  }
  return visited
}

/** The outlet order as rendered on each forking node, left→right. Layout must seat every arm's
 *  subtree under its own outlet — a machine-drawn canvas never gets to cross its wires. (This was
 *  a yes/no-only pairwise swap before; the 3-way call-result fork could come out criss-crossed —
 *  spotted by Patrick on a script-created doc 08-06.) */
const FORK_HANDLE_ORDER: Readonly<Partial<Record<WorkflowNode[`type`], readonly string[]>>> = {
  branch: [`yes`, `no`],
  waitForReply: [`yes`, `no`],
  waitForCallResult: [`answered`, `no_answer`, `voicemail`],
  waitUntil: [`yes`, `no`],
  // The main path is the unnamed outlet, so it matches an edge with no handle.
  createTask: [``, `task`],
}

/** A step's kind and data, still a union over kinds, so checking `type` narrows `data`. */
export type StepOf<N = WorkflowNode> = N extends WorkflowNode ? Pick<N, `type` | `data`> : never

/** A fork's exits left to right as drawn, or undefined for a step that does not fork. The one
 *  answer to "which exits does this step have", for layout and every canvas edit. */
export function forkHandles(node: StepOf): readonly string[] | undefined {
  if (node.type === `waitForKeyword`) return [...node.data.options.map((option) => option.id), ...FIXED_REPLY_EXITS]
  return FORK_HANDLE_ORDER[node.type]
}

/** Reseat each fork's arms in outlet order, moving whole subtrees by their horizontal extent (a
 *  nested branch is wider than a lone Exit). dagre's gaps aren't reused: it packs each rank on its
 *  own, so sibling subtrees overlap on lower ranks. Deepest forks first, so outer forks move
 *  settled subtrees. */
function orientForks(positions: Map<string, LayoutBox>, nodes: WorkflowNode[], edges: Edge[]) {
  const forks = nodes
    .filter((node) => forkHandles(node) && positions.has(node.id))
    .sort((a, b) => (positions.get(b.id)?.y ?? 0) - (positions.get(a.id)?.y ?? 0))
  for (const fork of forks) {
    const forkBox = positions.get(fork.id)
    if (!forkBox) continue
    const roots = (forkHandles(fork) ?? [])
      .map((handle) => edges.find((e) => e.source === fork.id && (e.sourceHandle ?? ``) === handle)?.target)
      .filter((id): id is string => !!id && positions.has(id))
    if (roots.length < 2) continue
    const armSets = roots.map((root) => collectDownstream(root, edges))
    // A node reachable from two arms (they merge) belongs to neither; it stays where dagre put it.
    const seen = new Map<string, number>()
    for (const set of armSets) for (const id of set) seen.set(id, (seen.get(id) ?? 0) + 1)
    if (roots.some((root) => seen.get(root) !== 1)) continue
    const arms = armSets.map((set, i) => {
      const boxes = [...set].filter((id) => seen.get(id) === 1).flatMap((id) => positions.get(id) ?? [])
      const rootBox = positions.get(roots[i])
      return {
        boxes,
        left: Math.min(...boxes.map((box) => box.x)),
        right: Math.max(...boxes.map((box) => box.x + box.width)),
        rootCenter: rootBox ? rootBox.x + rootBox.width / 2 : 0,
      }
    })
    // Pack left→right in outlet order, NODESEP apart, then center the outlets under the fork.
    const dxs: number[] = []
    let cursor = 0
    for (const arm of arms) {
      dxs.push(cursor - arm.left)
      cursor += arm.right - arm.left + NODESEP
    }
    const outletsMid = (arms[0].rootCenter + dxs[0] + arms[arms.length - 1].rootCenter + dxs[dxs.length - 1]) / 2
    const shift = forkBox.x + forkBox.width / 2 - outletsMid
    arms.forEach((arm, i) => {
      for (const box of arm.boxes) box.x += dxs[i] + shift
    })
  }
}

/** Nodes dagre places in the same rank share a center-y, but with different card heights they get
 *  different TOPs — so a branch's two arms look staggered. Snap every node in a rank to the topmost top
 *  in that rank, so cards always form clean, aligned rows regardless of their individual heights. */
function alignRankTops(positions: Map<string, { x: number; y: number }>, rankCenterY: Map<string, number>) {
  const byRank = new Map<number, string[]>()
  for (const [id, centerY] of rankCenterY) {
    const key = Math.round(centerY) // same-rank nodes share a center-y
    const group = byRank.get(key)
    if (group) group.push(id)
    else byRank.set(key, [id])
  }
  for (const ids of byRank.values()) {
    if (ids.length < 2) continue
    const top = Math.min(...ids.map((id) => positions.get(id)?.y ?? 0))
    for (const id of ids) {
      const pos = positions.get(id)
      if (pos) pos.y = top
    }
  }
}

/** Re-flow the graph top-to-bottom with dagre. Fork children fan out horizontally in their node's
 *  outlet order, same-rank cards align to a common top, and everything snaps to the grid. */
export function layoutFlow(nodes: WorkflowNode[], edges: Edge[]): WorkflowNode[] {
  if (nodes.length === 0) return nodes

  const graph = new dagre.graphlib.Graph()
  graph.setGraph({ rankdir: `TB`, nodesep: NODESEP, ranksep: 80 })
  graph.setDefaultEdgeLabel(() => ({}))

  for (const node of nodes) {
    graph.setNode(node.id, {
      width: node.measured?.width ?? FALLBACK_WIDTH,
      height: node.measured?.height ?? FALLBACK_HEIGHT,
    })
  }
  for (const edge of edges) graph.setEdge(edge.source, edge.target)

  dagre.layout(graph)

  // dagre positions are centers; React Flow positions are top-left. Keep the center-y to group ranks.
  const positions = new Map<string, LayoutBox>()
  const rankCenterY = new Map<string, number>()
  for (const node of nodes) {
    const laid = graph.node(node.id)
    if (laid) {
      positions.set(node.id, { x: laid.x - laid.width / 2, y: laid.y - laid.height / 2, width: laid.width })
      rankCenterY.set(node.id, laid.y)
    }
  }

  orientForks(positions, nodes, edges)
  alignRankTops(positions, rankCenterY)

  return nodes.map((node) => {
    const pos = positions.get(node.id)
    if (!pos) return node
    return { ...node, position: { x: snap(pos.x), y: snap(pos.y) } } as WorkflowNode
  })
}
